import { useEffect, useState } from 'react';
import { X, CheckCircle2, ChevronDown, ChevronRight, ExternalLink, Plus, Trash2 } from 'lucide-react';
import type { CategoriaVista, InsumoVista, ProductoVista } from '@shared/types';
import { NumberField } from '../ui/NumberField';
import { conConfirmacionDeNombre } from '@renderer/lib/confirmarNombreParecido';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { pesosACentavos, centavosAPesos } from '@shared/centavos';
import { FormInsumo } from './FormInsumo';

interface Props {
  abierto: boolean;
  producto: ProductoVista | null;
  categorias: CategoriaVista[];
  onClose: () => void;
  onSaved: () => void;
}

const UNIDADES_VENTA = ['pieza', 'kg', 'litro', 'porcion'] as const;
type UnidadVenta = (typeof UNIDADES_VENTA)[number];

/** Sprint 62 — En palabras del negocio; "porcion" a secas no decía nada. */
const ETIQUETA_UNIDAD_VENTA: Record<UnidadVenta, string> = {
  pieza: 'Por pieza (una lata, un pan, una orden)',
  kg: 'Por kilo (se pesa)',
  litro: 'Por litro (se mide)',
  porcion: 'Por porción (un plato servido)',
};

export function FormProducto({ abierto, producto, categorias, onClose, onSaved }: Props) {
  const [form, setForm] = useState({
    nombre: '',
    categoriaId: undefined as number | undefined,
    descripcion: '',
    precio: 0,
    precioEnPaquete: undefined as number | undefined,
    habilitado: true,
    costoBase: undefined as number | undefined,
    unidadVenta: 'pieza' as UnidadVenta,
    insumoId: undefined as number | undefined,
    gramaje: undefined as number | undefined,
  });
  /**
   * Sprint 63 (Fase 2b) — Ingredientes extra, más allá del principal.
   *
   * Un producto podía salir de UN ingrediente. Alcanza para una porción de picaña, pero
   * no para un pan con ajo (pan + ajo + mantequilla) ni para una hamburguesa — y eso es
   * lo que impedía vender el sistema a cualquier giro que prepare platillos.
   *
   * La pantalla conserva el caso simple arriba, intacto, y los demás ingredientes se
   * agregan sólo si hacen falta: quien vende cortes no ve un editor de recetas.
   */
  const [extras, setExtras] = useState<Array<{ insumoId: number; cantidad: number }>>([]);
  const [insumos, setInsumos] = useState<InsumoVista[]>([]);
  /**
   * Sprint 62 — Costo, precio en paquete y descripción viven detrás de "Más
   * opciones". No son parte de capturar el menú: son ajustes finos, y en pantalla
   * competían por atención con el precio y el ingrediente, que sí importan.
   */
  const [mostrarMas, setMostrarMas] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  /**
   * Sprint 109 — Crear un ingrediente sin salir de aquí.
   *
   * Es la contraparte de haber cerrado el alta de ingredientes en Inventario, y sin esto aquel
   * cierre dejaría un flujo real sin salida: el queso de una torta **no es** algo que se venda, así
   * que no puede nacer como producto — pero tiene que poder nacer.
   *
   * Nace donde se usa. Y eso no es un rodeo: un ingrediente que existe colgado de algo que se vende
   * no puede quedar huérfano, que es exactamente lo que le pasó a la Pepsi del cliente. El hueco
   * que empujaba a la gente a Inventario era éste — un desplegable que sólo sabía elegir.
   *
   * Guarda a cuál renglón vuelve: `'principal'` o el índice del extra.
   */
  const [creandoIngrediente, setCreandoIngrediente] = useState<'principal' | number | null>(null);

  const cargarInsumos = async () => {
    const res = await window.api.insumos.listar();
    if (res.ok) setInsumos(res.data.filter((i) => i.activo));
    return res.ok ? res.data : [];
  };

  useEffect(() => {
    if (!abierto) return;
    void cargarInsumos();
  }, [abierto]);

  /**
   * Al volver del alta, el ingrediente recién creado queda **elegido** en el renglón que lo pidió.
   *
   * Se identifica por el id más alto: acaba de crearse, así que es el último. Hacerlo de otro modo
   * exigiría que el canal devolviera el insumo creado, y dejar a alguien eligiendo a mano de una
   * lista de treinta algo que tecleó hace dos segundos es justo la fricción que esto viene a quitar.
   */
  const alCrearIngrediente = async () => {
    const destino = creandoIngrediente;
    setCreandoIngrediente(null);
    const lista = await cargarInsumos();
    const nuevo = lista.filter((i) => i.activo).sort((a, b) => b.id - a.id)[0];
    if (!nuevo || destino === null) return;
    if (destino === 'principal') elegirInsumo(nuevo.id);
    else {
      setExtras((prev) =>
        prev.map((x, k) => (k === destino ? { ...x, insumoId: nuevo.id } : x)),
      );
    }
  };

  useEffect(() => {
    if (producto) {
      setForm({
        nombre: producto.nombre,
        categoriaId: producto.categoriaId ?? undefined,
        descripcion: producto.descripcion ?? '',
        // Sprint 39 — Centavos en state, pesos solo en el input UI.
        precio: producto.precio,
        precioEnPaquete: producto.precioEnPaquete ?? undefined,
        habilitado: producto.habilitado,
        costoBase: producto.costoBase ?? undefined,
        unidadVenta: ((producto.unidadVenta as UnidadVenta) ?? 'pieza'),
        insumoId: producto.insumoId ?? undefined,
        gramaje: producto.gramaje ?? undefined,
      });
      // La receta guardada: el primer renglón va al campo principal, el resto abajo.
      const receta = producto.receta ?? [];
      if (receta.length > 1) {
        setForm((f) => ({ ...f, insumoId: receta[0].insumoId, gramaje: receta[0].cantidad }));
        setExtras(receta.slice(1).map((r) => ({ insumoId: r.insumoId, cantidad: r.cantidad })));
      } else {
        setExtras([]);
      }
    } else {
      setForm({
        nombre: '',
        categoriaId: undefined,
        descripcion: '',
        precio: 0,
        precioEnPaquete: undefined,
        habilitado: true,
        costoBase: undefined,
        unidadVenta: 'pieza',
        insumoId: undefined,
        gramaje: undefined,
      });
    }
    setError(null);
    setMostrarMas(false);
  }, [producto, abierto]);

  useEffect(() => {
    if (!abierto || producto) return;
    setExtras([]);
  }, [abierto, producto]);

  if (!abierto) return null;

  const insumoSeleccionado = form.insumoId
    ? insumos.find((i) => i.id === form.insumoId)
    : null;
  const tieneVinculo = !!form.insumoId;

  /**
   * Sprint 62 — Elegir el ingrediente propone la unidad y la porción.
   *
   * Que la unidad de venta del producto y la del insumo no coincidan es de donde
   * salieron los peores defectos de los Sprints 60 y 61 (una lata que descontaba 20
   * latas, un refresco ligado a frijoles). Igualarlas por default y proponer 1 a 1
   * hace que el caso común —una lata sale de una lata— no tenga nada que capturar.
   */
  const elegirInsumo = (id: number | undefined) => {
    if (!id) {
      setForm({ ...form, insumoId: undefined, gramaje: undefined });
      return;
    }
    const ins = insumos.find((i) => i.id === id);
    const mismaUnidad = ins ? (ins.unidad as string) === form.unidadVenta : false;
    setForm({
      ...form,
      insumoId: id,
      unidadVenta: (ins?.unidad as UnidadVenta) ?? form.unidadVenta,
      // Misma unidad ⇒ uno a uno. Distinta ⇒ lo decide ella (0.25 kg por orden).
      gramaje: form.gramaje ?? (mismaUnidad || !ins ? 1 : undefined),
    });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.nombre.trim().length < 1) {
      setError('El nombre es obligatorio');
      return;
    }
    if (form.precio <= 0) {
      setError('El precio debe ser mayor a 0');
      return;
    }
    if (form.insumoId && (!form.gramaje || form.gramaje <= 0)) {
      setError('Falta indicar cuánto de ese ingrediente se lleva cada venta.');
      return;
    }
    if (extras.some((e) => !e.insumoId || e.cantidad <= 0)) {
      setError('Cada ingrediente extra necesita cuál es y cuánto lleva.');
      return;
    }
    const ids = [form.insumoId, ...extras.map((e) => e.insumoId)].filter(Boolean);
    if (new Set(ids).size !== ids.length) {
      setError('Un ingrediente está dos veces en la receta.');
      return;
    }
    setEnviando(true);
    /**
     * Sprint 62 — Ya no se manda `tipo` (nadie lo leía en toda la aplicación) ni el
     * stock del producto: el inventario vive en el insumo, y un producto sin insumo
     * simplemente no lleva cuenta.
     *
     * Sprint 39 — state ya en centavos (NumberField convierte para UX en pesos).
     */
    const data = {
      nombre: form.nombre.trim(),
      categoriaId: form.categoriaId,
      descripcion: form.descripcion || undefined,
      precio: form.precio,
      precioEnPaquete: form.precioEnPaquete,
      habilitado: form.habilitado,
      costoBase: form.costoBase,
      unidadVenta: form.unidadVenta,
      insumoId: form.insumoId,
      gramaje: form.gramaje,
      /**
       * Sprint 63 — La receta manda. Con un ingrediente equivale al vínculo de siempre;
       * el backend mantiene `insumoId`/`gramaje` en sintonía para el código que aún los
       * lee.
       */
      receta: form.insumoId
        ? [
            { insumoId: form.insumoId, cantidad: form.gramaje ?? 1 },
            ...extras.filter((e) => e.insumoId > 0 && e.cantidad > 0),
          ]
        : [],
    };
    const res = producto
      ? await window.api.productos.actualizar(producto.id, data)
      : await conConfirmacionDeNombre((confirmado) =>
          window.api.productos.crear({ ...data, nombreParecidoConfirmado: confirmado || undefined } as unknown as Omit<ProductoVista, 'id'>),
        );
    setEnviando(false);
    if (res.ok) onSaved();
    else setError(traducirErrorBackend(res.error, res.codigo));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 sticky top-0 bg-white">
          <h2 className="text-lg font-semibold">
            {producto ? 'Editar producto' : 'Nuevo producto'}
          </h2>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4 px-6 py-5">
          {/* ─────────── 1. Lo que se vende ─────────── */}
          <div>
            <label className="label" htmlFor="prodNombre">
              ¿Qué se vende? *
            </label>
            <input
              id="prodNombre"
              className="input"
              placeholder="Ej: Espada de Sirloin, Coca Cola lata, Orden de papas"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              autoFocus
            />
            <p className="mt-1 text-xs text-slate-500">
              Así aparecerá en el botón del punto de venta y en el ticket del cliente.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="label" htmlFor="prodPrecio">Precio de venta *</label>
              {/* Sprint 39 — UX en pesos, state en centavos. */}
              <NumberField
                id="prodPrecio"
                min={0}
                decimals={2}
                className="input"
                value={centavosAPesos(form.precio)}
                onChange={(n) => setForm({ ...form, precio: pesosACentavos(n) })}
              />
            </div>
            <div>
              <label className="label" htmlFor="prodCat">Categoría</label>
              <select
                id="prodCat"
                className="input"
                value={form.categoriaId ?? ''}
                onChange={(e) =>
                  setForm({ ...form, categoriaId: e.target.value ? Number(e.target.value) : undefined })
                }
              >
                <option value="">Sin categoría</option>
                {categorias.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">
                {categorias.length === 0
                  ? 'Todavía no hay categorías. Sirven para agrupar los botones del punto de venta.'
                  : 'Agrupa los botones del punto de venta.'}
              </p>
            </div>
          </div>

          {/* ─────────── 2. Inventario ───────────
              Sprint 62 — Una sola pregunta, en lenguaje del negocio. Antes eran dos
              bloques —"Vínculo a inventario por insumo" con la palabra legacy, más
              stock manual del producto— que ofrecían dos sistemas distintos a quien
              no sabe que existen, y el segundo estaba muerto desde el Sprint 7. */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
            <label className="label" htmlFor="prodInsumo">
              ¿De qué ingrediente sale?
            </label>
            <select
              id="prodInsumo"
              className="input"
              value={form.insumoId ?? ''}
              onChange={(e) => elegirInsumo(e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">No descontar de ningún ingrediente</option>
              {insumos.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.nombre} — hay {i.stockActual} {i.unidad}
                </option>
              ))}
            </select>
            {/*
              Sprint 109 — La salida cuando el ingrediente no está en la lista.
              Antes había que cerrar esto, ir a Inventario, crearlo y volver a empezar el producto.
            */}
            <button
              type="button"
              onClick={() => setCreandoIngrediente('principal')}
              className="inline-flex min-h-[44px] items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              <Plus className="h-4 w-4" />
              ¿No está en la lista? Crear el ingrediente
            </button>

            {tieneVinculo && insumoSeleccionado ? (
              <>
                <PorcionServidaCampo
                  insumoNombre={insumoSeleccionado.nombre}
                  insumoUnidad={insumoSeleccionado.unidad}
                  mermaCoccionPct={insumoSeleccionado.mermaCoccionPct ?? 0}
                  value={form.gramaje ?? 0}
                  onChange={(n) => setForm({ ...form, gramaje: n > 0 ? n : undefined })}
                />
                {/*
                  Sprint 63 — Ingredientes extra. Aparecen sólo si se piden: quien vende
                  cortes no debería ver un editor de recetas para capturar una picaña.
                */}
                {extras.map((extra, i) => {
                  const ins = insumos.find((x) => x.id === extra.insumoId);
                  return (
                    <div key={i} className="grid grid-cols-[1fr_auto_auto] items-end gap-2">
                      <div>
                        <label className="label" htmlFor={`extra-insumo-${i}`}>
                          También lleva
                        </label>
                        <select
                          id={`extra-insumo-${i}`}
                          className="input"
                          value={extra.insumoId || ''}
                          onChange={(e) =>
                            setExtras((prev) =>
                              prev.map((x, k) =>
                                k === i ? { ...x, insumoId: Number(e.target.value) } : x,
                              ),
                            )
                          }
                        >
                          <option value="">Elige el ingrediente…</option>
                          {insumos
                            .filter((x) => x.id !== form.insumoId)
                            .map((x) => (
                              <option key={x.id} value={x.id}>
                                {x.nombre} — hay {x.stockActual} {x.unidad}
                              </option>
                            ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setCreandoIngrediente(i)}
                          className="mt-1 inline-flex min-h-[44px] items-center gap-1 text-xs font-medium text-primary hover:underline"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Crear este ingrediente
                        </button>
                      </div>
                      <div>
                        <label className="label" htmlFor={`extra-cant-${i}`}>
                          Cuánto {ins ? `(${ins.unidad})` : ''}
                        </label>
                        <NumberField
                          id={`extra-cant-${i}`}
                          min={0}
                          decimals={3}
                          className="input w-28 text-right font-mono"
                          value={extra.cantidad}
                          ariaLabel={`Cantidad del ingrediente extra ${i + 1}`}
                          onChange={(n) =>
                            setExtras((prev) => prev.map((x, k) => (k === i ? { ...x, cantidad: n } : x)))
                          }
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setExtras((prev) => prev.filter((_, k) => k !== i))}
                        className="flex h-11 w-11 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-600"
                        aria-label={`Quitar el ingrediente extra ${i + 1}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={() => setExtras((prev) => [...prev, { insumoId: 0, cantidad: 0 }])}
                  className="inline-flex min-h-[44px] items-center gap-1 text-sm font-medium text-primary hover:underline"
                >
                  <Plus className="h-4 w-4" />
                  {extras.length === 0
                    ? '¿Lleva otro ingrediente? (pan con ajo, hamburguesa…)'
                    : 'Agregar otro ingrediente'}
                </button>

                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>El sistema llevará la cuenta solo</span>
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-emerald-800">
                    Cada venta descontará de <strong>{insumoSeleccionado.nombre}</strong>
                    {extras.length > 0 ? ' y de los demás ingredientes' : ''}, y el producto se
                    marcará agotado cuando ya no alcance
                    {extras.length > 0 ? ' alguno de ellos' : ''}. Hoy hay{' '}
                    <strong>
                      {insumoSeleccionado.stockActual} {insumoSeleccionado.unidad}
                    </strong>
                    ; se carga y se corrige en{' '}
                    <a
                      href="#/inventario"
                      className="inline-flex items-center gap-0.5 font-medium underline"
                      onClick={onClose}
                    >
                      Inventario <ExternalLink className="h-3 w-3" />
                    </a>
                    .
                  </p>
                </div>
              </>
            ) : (
              <p className="text-xs leading-relaxed text-slate-600">
                {insumos.length === 0
                  ? 'Todavía no hay ingredientes capturados, y no hace falta para empezar: este producto '
                  : 'Sin ingrediente ligado, este producto '}
                se podrá vender <strong>siempre</strong>, y el sistema no llevará la cuenta de
                cuántos quedan.
              </p>
            )}
          </div>

          {/* ─────────── 3. Ajustes finos, escondidos a propósito ─────────── */}
          <button
            type="button"
            onClick={() => setMostrarMas((v) => !v)}
            className="flex min-h-[44px] w-full items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            {mostrarMas ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Más opciones (cómo se vende, costo, precio en paquete, descripción)
          </button>

          {mostrarMas && (
            <div className="space-y-3 rounded-lg border border-slate-200 p-4">
              <div>
                <label className="label" htmlFor="prodUnidadVenta">¿Cómo se vende?</label>
                <select
                  id="prodUnidadVenta"
                  className="input"
                  value={form.unidadVenta}
                  onChange={(e) => setForm({ ...form, unidadVenta: e.target.value as UnidadVenta })}
                >
                  {UNIDADES_VENTA.map((u) => (
                    <option key={u} value={u}>
                      {ETIQUETA_UNIDAD_VENTA[u]}
                    </option>
                  ))}
                </select>
                {insumoSeleccionado &&
                  (insumoSeleccionado.unidad as string) !== form.unidadVenta && (
                    <p className="mt-1 text-xs font-medium text-amber-800">
                      Ojo: se vende por {form.unidadVenta} y el ingrediente se mide en{' '}
                      {insumoSeleccionado.unidad}. Revisa que la cantidad por venta de arriba esté
                      en {insumoSeleccionado.unidad}.
                    </p>
                  )}
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="label" htmlFor="prodCosto">Lo que te cuesta (opcional)</label>
                  {/* Sprint 39 — UX en pesos, state en centavos. */}
                  <NumberField
                    id="prodCosto"
                    min={0}
                    decimals={2}
                    className="input"
                    value={form.costoBase != null ? centavosAPesos(form.costoBase) : 0}
                    onChange={(n) => setForm({ ...form, costoBase: n > 0 ? pesosACentavos(n) : undefined })}
                  />
                  <p className="mt-1 text-xs text-slate-500">Sólo para el reporte de ganancia.</p>
                </div>
                <div>
                  <label className="label" htmlFor="prodPrecioPaq">
                    Precio dentro de un paquete (opcional)
                  </label>
                  {/* Sprint 39 — UX en pesos, state en centavos. */}
                  <NumberField
                    id="prodPrecioPaq"
                    min={0}
                    decimals={2}
                    className="input"
                    value={form.precioEnPaquete != null ? centavosAPesos(form.precioEnPaquete) : 0}
                    onChange={(n) => setForm({ ...form, precioEnPaquete: n > 0 ? pesosACentavos(n) : undefined })}
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Si se deja en 0, usa el precio de venta normal.
                  </p>
                </div>
              </div>

              <div>
                <label className="label" htmlFor="prodDesc">Descripción (opcional)</label>
                <textarea
                  id="prodDesc"
                  className="input"
                  rows={2}
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                />
              </div>
            </div>
          )}

          <label className="flex min-h-[44px] cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={form.habilitado}
              onChange={(e) => setForm({ ...form, habilitado: e.target.checked })}
              className="h-5 w-5"
            />
            <span className="text-sm">Se puede vender ahora</span>
          </label>


          {error && <div className="error-text">{error}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost" disabled={enviando}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {producto ? 'Guardar cambios' : 'Crear producto'}
            </button>
          </div>
        </form>
      </div>

      {/*
        Sprint 109 — El alta de ingrediente, montada **encima** del producto que lo va a usar.

        Es el único lugar del sistema desde donde se crea un ingrediente: aquí el contexto ya dice
        para qué es, y al cerrarse queda elegido en el renglón que lo pidió. Comparado con lo que
        había —crearlo suelto en Inventario y descubrir tres días después que no se puede vender—
        la diferencia no es de pasos, es de si el dato nace conectado o huérfano.
      */}
      <FormInsumo
        abierto={creandoIngrediente !== null}
        insumo={null}
        onClose={() => setCreandoIngrediente(null)}
        onSaved={() => void alCrearIngrediente()}
      />
    </div>
  );
}

// ============================================================================
// Sprint 13 — Porción servida. La merma de cocción es atributo del insumo
// (sprint 13), no de cada producto. Aquí solo se captura la porción que el
// cliente recibe en su plato.
// ============================================================================

function PorcionServidaCampo({
  insumoNombre,
  insumoUnidad,
  mermaCoccionPct,
  value,
  onChange,
}: {
  insumoNombre: string;
  insumoUnidad: string;
  mermaCoccionPct: number;
  value: number;
  onChange: (n: number) => void;
}) {
  const tieneMerma = mermaCoccionPct > 0;
  const factorCrudo = tieneMerma ? 1 / (1 - mermaCoccionPct / 100) : 1;
  const crudoEquivalente = value * factorCrudo;

  return (
    <div>
      <label className="label" htmlFor="prodConsumo">
        {/* Sprint 62 — "Porción servida COCIDA por venta" era jerga: la misma
            pregunta dicha como la diría alguien de la cocina. */}
        {tieneMerma
          ? `¿Cuánto ${insumoNombre} lleva cada orden, ya cocido? *`
          : `¿Cuánto ${insumoNombre} lleva cada venta? *`}
      </label>
      <div className="flex gap-2 items-center">
        <NumberField
          id="prodConsumo"
          min={0.001}
          decimals={3}
          className="input flex-1 font-mono text-right"
          value={value}
          onChange={onChange}
          placeholder="0.000"
        />
        <span className="text-sm text-slate-600 whitespace-nowrap">
          {insumoUnidad} {tieneMerma ? 'cocido' : ''}
        </span>
      </div>
      {tieneMerma ? (
        <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs">
          <strong>{insumoNombre}</strong> pierde <strong>{mermaCoccionPct}%</strong> al cocinarse, así
          que del almacén sale más de lo que llega al plato.
          {value > 0 && (
            <div className="mt-1 text-amber-800">
              Cada venta descontará{' '}
              <strong>
                {crudoEquivalente.toFixed(3)} {insumoUnidad}
              </strong>{' '}
              crudos para servir {value} {insumoUnidad} en el plato.
            </div>
          )}
          <div className="mt-1 text-slate-500">
            Ese porcentaje se cambia en el ingrediente, no aquí.
          </div>
        </div>
      ) : (
        <p className="mt-1 text-xs text-slate-500">
          Lo que sale del almacén cada vez que se vende esto. Si es uno por uno —una lata sale de
          una lata— déjalo en 1.
        </p>
      )}
    </div>
  );
}
